import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface GeolocationSettings {
  geo_enabled: boolean;
  geo_strict_mode: boolean;
  geo_default_radius_meters: number;
}

const DEFAULTS: GeolocationSettings = {
  geo_enabled: false,
  geo_strict_mode: false,
  geo_default_radius_meters: 100,
};

export const geolocationKeys = {
  all: ['geolocation'] as const,
  settings: (companyId: string | undefined) => 
    [...geolocationKeys.all, 'settings', companyId] as const,
  geofences: (companyId: string | undefined, filter: string) => 
    [...geolocationKeys.all, 'geofences', companyId, filter] as const,
};

async function fetchGeolocationSettings(companyId: string | undefined): Promise<GeolocationSettings> {
  if (!companyId) {
    return DEFAULTS;
  }

  const { data, error } = await supabase
    .from("company_settings")
    .select("geo_enabled, geo_strict_mode, geo_default_radius_meters")
    .eq("company_id", companyId)
    .single();

  if (error) {
    console.error("Error fetching geo settings:", error);
    return DEFAULTS;
  }

  return {
    geo_enabled: data.geo_enabled ?? false,
    geo_strict_mode: data.geo_strict_mode ?? false,
    geo_default_radius_meters: data.geo_default_radius_meters ?? 100,
  };
}

export function useGeolocationSettingsQuery(companyId: string | undefined) {
  return useQuery({
    queryKey: geolocationKeys.settings(companyId),
    queryFn: () => fetchGeolocationSettings(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
}
